import React, { useMemo, useState } from 'react';
import classnames from 'classnames';

import Wahanda from 'common/wahanda';
import { Radio } from 'components/common/Radio';
import Dialog from 'components/common/react-dialog';
import { track } from 'src/tracking/trackingService';

import style from './ExportReasonDialog.scss';

const lang = Wahanda.lang.clients.exportReasonDialog;

export type ExportReason =
  | 'backup'
  | 'exploring'
  | 'switching'
  | 'other-tool'
  | 'closing'
  | 'other';

interface ExportReasonOption {
  value: ExportReason;
  label: string;
}

const EXPORT_REASON_OPTIONS: ExportReasonOption[] = [
  { value: 'backup', label: lang.options.backup },
  { value: 'exploring', label: lang.options.exploring },
  { value: 'switching', label: lang.options.switching },
  { value: 'other-tool', label: lang.options.otherTool },
  { value: 'closing', label: lang.options.closing },
  { value: 'other', label: lang.options.other },
];

function shuffleArray<T>(items: T[]): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export interface ExportReasonDialogProps {
  onExport: (reason: ExportReason) => void;
  onCancel: () => void;
}

export const ExportReasonDialog = ({ onExport, onCancel }: ExportReasonDialogProps) => {
  const [selectedReason, setSelectedReason] = useState<ExportReason | null>(null);

  const displayOptions = useMemo(() => {
    const otherOption = EXPORT_REASON_OPTIONS.find((option) => option.value === 'other');
    const optionsToShuffle = EXPORT_REASON_OPTIONS.filter((option) => option.value !== 'other');
    const shuffled = shuffleArray(optionsToShuffle);
    return otherOption ? [...shuffled, otherOption] : shuffled;
  }, []);

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedReason(event.target.value as ExportReason);
  };

  const handleExportClick = () => {
    if (selectedReason) {
      track('clients.management.client_list.export_reason.selected', {
        reason: selectedReason,
      });
      onExport(selectedReason);
    }
  };

  const handleClose = () => {
    track('clients.management.client_list.export_reason.canceled');
    onCancel();
  };

  const isExportDisabled = !selectedReason;

  return (
    <Dialog dataTest="export-reason-dialog" onClose={handleClose} title={lang.title} width={500}>
      <div className={style.container}>
        <p className={style.description}>{lang.description}</p>
        <div className={style.optionsList}>
          {displayOptions.map((option) => (
            <div key={option.value} className={style.optionItem}>
              <Radio
                dataTest={`export-reason-${option.value}`}
                name="export-reason"
                value={option.value}
                label={option.label}
                checked={selectedReason === option.value}
                onChange={handleReasonChange}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="dialog-actions">
        <a
          href="javascript:;"
          className="button-cancel button-secondary-plain"
          onClick={handleClose}
          data-test="export-reason-cancel"
        >
          {lang.buttons.cancel}
        </a>
        <div className="buttons-right">
          <button
            type="button"
            className={classnames('button button-primary', style.exportButton, {
              disabled: isExportDisabled,
            })}
            disabled={isExportDisabled}
            onClick={handleExportClick}
            data-test="export-reason-export"
          >
            {lang.buttons.export}
          </button>
        </div>
      </div>
    </Dialog>
  );
};
